/**
 * Pre-capture metadata gathering for photo uploads.
 *
 * Collects device, environment, session, location, agricultural cycle/batch
 * state, and weather signals at the moment a photo is taken. The output is
 * sent alongside the photo upload so backend fraud detection and AI analysis
 * pipelines can correlate behavior across sessions.
 *
 * Handles both crop (cycleId) and poultry (batchId) paths internally.
 * All fields fail gracefully — missing browser APIs, denied permissions, or
 * network errors yield `undefined`/`null` rather than throwing. No failure
 * mode blocks the photo from uploading.
 *
 * schema_version 2 adds `agricultural_context` and `weather` top-level keys on
 * top of the v1 shape. All v1 fields remain unchanged.
 */

import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

type PhotoCategory = Database["public"]["Enums"]["photo_category"];

export interface PreCaptureInput {
  userId: string;
  cycleId?: string;
  batchId?: string;
  taskId?: string;
  photoCategory: PhotoCategory | string;
  supabaseClient: SupabaseClient<Database>;
  sessionId: string;
  sessionStartedAt: string;
  cameraOpenedAt: number;
  sessionPhotoCount: number;
  isFrontCamera?: boolean;
  flashMode?: "on" | "off" | "auto" | null;
  zoomLevel?: number | null;
}

export interface PreCaptureAgriculturalContext {
  cycle_day: number | null;
  total_cycle_days: number | null;
  normalized_day: number | null;
  expected_stage_name: string | null;
  expected_stage_order: number | null;
  expected_activities: string[] | null;
  previous_photo_days_ago: number | null;
  photos_in_cycle_so_far: number | null;
  active_cycle_count: number | null;
  farmer_experience_years: number | null;
  farmer_region: string | null;
  farmer_country_code: string | null;
  crop_name: string | null;
  crop_category: string | null;
}

export interface PreCaptureWeather {
  temp_c: number | null;
  feels_like_c: number | null;
  humidity_pct: number | null;
  pressure_hpa: number | null;
  wind_speed_mps: number | null;
  wind_direction_deg: number | null;
  cloud_cover_pct: number | null;
  visibility_meters: number | null;
  weather_main: string | null;
  weather_description: string | null;
  rain_1h_mm: number | null;
  uv_index: number | null;
  sunrise_utc: string | null;
  sunset_utc: string | null;
  is_daytime: boolean | null;
  data_source: "openweathermap";
  fetched_at: string;
}

export interface PreCaptureMetadata {
  schema_version: 2;

  // Session
  session_id: string;
  session_started_at: string;
  session_photo_count: number;
  time_in_camera_ms: number;

  // Context
  user_id: string;
  cycle_id?: string;
  batch_id?: string;
  task_id?: string;
  photo_category: string;

  // Device
  device: {
    user_agent: string;
    platform: string;
    language: string;
    timezone: string;
    screen_width: number;
    screen_height: number;
    pixel_ratio: number;
    online: boolean;
    connection_type?: string;
    downlink_mbps?: number;
    rtt_ms?: number;
    save_data?: boolean;
    memory_gb?: number;
    hardware_concurrency?: number;
  };

  // Camera
  camera: {
    is_front_camera?: boolean;
    flash_mode?: string;
    zoom_level?: number;
  };

  // Geolocation (best-effort, may be undefined)
  location?: {
    latitude: number;
    longitude: number;
    accuracy_m: number;
    altitude?: number | null;
    heading?: number | null;
    speed?: number | null;
    captured_at: string;
  };

  // Time
  client_timestamp: string;
  client_timestamp_ms: number;

  // New in schema_version 2 — agricultural state + weather snapshot
  agricultural_context: PreCaptureAgriculturalContext | null;
  weather: PreCaptureWeather | null;
}

const safeGet = <T>(fn: () => T): T | undefined => {
  try {
    return fn();
  } catch {
    return undefined;
  }
};

const getGeolocation = (): Promise<PreCaptureMetadata["location"]> => {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(undefined);
      return;
    }
    const timer = setTimeout(() => resolve(undefined), 3000);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          altitude: pos.coords.altitude,
          heading: pos.coords.heading,
          speed: pos.coords.speed,
          captured_at: new Date(pos.timestamp).toISOString(),
        });
      },
      () => {
        clearTimeout(timer);
        resolve(undefined);
      },
      { enableHighAccuracy: true, timeout: 2500, maximumAge: 60_000 }
    );
  });
};

async function gatherAgriculturalContext(
  input: PreCaptureInput
): Promise<PreCaptureAgriculturalContext> {
  const context: PreCaptureAgriculturalContext = {
    cycle_day: null,
    total_cycle_days: null,
    normalized_day: null,
    expected_stage_name: null,
    expected_stage_order: null,
    expected_activities: null,
    previous_photo_days_ago: null,
    photos_in_cycle_so_far: null,
    active_cycle_count: null,
    farmer_experience_years: null,
    farmer_region: null,
    farmer_country_code: null,
    crop_name: null,
    crop_category: null,
  };

  const sb = input.supabaseClient;

  try {
    const { data: profile } = await sb
      .from("profiles")
      .select("farming_experience_years, region, country_id, primary_crop_id")
      .eq("id", input.userId)
      .maybeSingle();
    const profileRow = profile as {
      farming_experience_years?: number | null;
      region?: string | null;
      country_id?: string | null;
      primary_crop_id?: string | null;
    } | null;

    if (profileRow) {
      context.farmer_experience_years = profileRow.farming_experience_years ?? null;
      context.farmer_region = profileRow.region ?? null;

      if (profileRow.country_id) {
        try {
          const { data: country } = await sb
            .from("countries")
            .select("code")
            .eq("id", profileRow.country_id)
            .maybeSingle();
          context.farmer_country_code =
            (country as { code?: string | null } | null)?.code ?? null;
        } catch {
          // non-fatal
        }
      }

      if (profileRow.primary_crop_id) {
        try {
          const { data: crop } = await sb
            .from("crops")
            .select("name, category")
            .eq("id", profileRow.primary_crop_id)
            .maybeSingle();
          const cropRow = crop as { name?: string | null; category?: string | null } | null;
          context.crop_name = cropRow?.name ?? null;
          context.crop_category = cropRow?.category ?? null;
        } catch {
          // non-fatal
        }
      }
    }
  } catch {
    // non-fatal
  }

  if (input.cycleId) {
    try {
      const { data: cycle } = await sb
        .from("farmer_cycles")
        .select("start_date, cycle_id")
        .eq("id", input.cycleId)
        .maybeSingle();
      const cycleRow = cycle as { start_date?: string | null; cycle_id?: string | null } | null;

      if (cycleRow?.start_date) {
        const startDate = new Date(cycleRow.start_date);
        const dayDiff = Math.floor((Date.now() - startDate.getTime()) / 86_400_000) + 1;
        context.cycle_day = dayDiff;

        if (cycleRow.cycle_id) {
          try {
            const { data: cropCycle } = await sb
              .from("crop_cycles")
              .select("total_days")
              .eq("id", cycleRow.cycle_id)
              .maybeSingle();
            const cropCycleRow = cropCycle as { total_days?: number | null } | null;
            if (cropCycleRow?.total_days) {
              context.total_cycle_days = cropCycleRow.total_days;
              context.normalized_day = Math.min(1.0, dayDiff / cropCycleRow.total_days);
            }
          } catch {
            // non-fatal
          }

          try {
            const { data: stages } = await sb
              .from("cycle_stages")
              .select("name, stage_order, start_day, end_day, expected_activity_types")
              .eq("cycle_id", cycleRow.cycle_id)
              .order("stage_order", { ascending: true });
            const stageRows = (stages ?? []) as Array<{
              name?: string | null;
              stage_order?: number | null;
              start_day?: number | null;
              end_day?: number | null;
              expected_activity_types?: string[] | null;
            }>;
            const currentStage = stageRows.find(
              (s) =>
                s.start_day != null &&
                s.end_day != null &&
                dayDiff >= s.start_day &&
                dayDiff <= s.end_day
            );
            if (currentStage) {
              context.expected_stage_name = currentStage.name ?? null;
              context.expected_stage_order = currentStage.stage_order ?? null;
              context.expected_activities = currentStage.expected_activity_types ?? null;
            }
          } catch {
            // non-fatal
          }
        }
      }
    } catch {
      // non-fatal
    }

    try {
      const { count } = await sb
        .from("photos")
        .select("id", { count: "exact", head: true })
        .eq("user_id", input.userId)
        .eq("farmer_cycle_id", input.cycleId);
      context.photos_in_cycle_so_far = count ?? 0;
    } catch {
      // non-fatal
    }

    try {
      const { data: lastPhoto } = await sb
        .from("photos")
        .select("created_at")
        .eq("user_id", input.userId)
        .eq("farmer_cycle_id", input.cycleId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      const lastRow = lastPhoto as { created_at?: string | null } | null;
      if (lastRow?.created_at) {
        context.previous_photo_days_ago = Math.floor(
          (Date.now() - new Date(lastRow.created_at).getTime()) / 86_400_000
        );
      }
    } catch {
      // non-fatal
    }
  }

  if (input.batchId) {
    try {
      const { data: batch } = await sb
        .from("batches")
        .select("placement_date, breed_id")
        .eq("id", input.batchId)
        .maybeSingle();
      const batchRow = batch as {
        placement_date?: string | null;
        breed_id?: string | null;
      } | null;

      if (batchRow?.placement_date) {
        const placementDate = new Date(batchRow.placement_date);
        const dayDiff = Math.floor((Date.now() - placementDate.getTime()) / 86_400_000) + 1;
        context.cycle_day = dayDiff;

        if (batchRow.breed_id) {
          try {
            const { data: breed } = await sb
              .from("breeds")
              .select(
                "default_cycle_days, brooding_end_day, early_growth_end_day, late_growth_end_day"
              )
              .eq("id", batchRow.breed_id)
              .maybeSingle();
            const breedRow = breed as {
              default_cycle_days?: number | null;
              brooding_end_day?: number | null;
              early_growth_end_day?: number | null;
              late_growth_end_day?: number | null;
            } | null;
            if (breedRow?.default_cycle_days) {
              context.total_cycle_days = breedRow.default_cycle_days;
              context.normalized_day = Math.min(1.0, dayDiff / breedRow.default_cycle_days);
            }
            if (breedRow) {
              if (breedRow.brooding_end_day != null && dayDiff <= breedRow.brooding_end_day) {
                context.expected_stage_name = "brooding";
                context.expected_stage_order = 1;
              } else if (
                breedRow.early_growth_end_day != null &&
                dayDiff <= breedRow.early_growth_end_day
              ) {
                context.expected_stage_name = "early_growth";
                context.expected_stage_order = 2;
              } else if (
                breedRow.late_growth_end_day != null &&
                dayDiff <= breedRow.late_growth_end_day
              ) {
                context.expected_stage_name = "late_growth";
                context.expected_stage_order = 3;
              } else {
                context.expected_stage_name = "finishing";
                context.expected_stage_order = 4;
              }
            }
          } catch {
            // non-fatal
          }
        }
      }
    } catch {
      // non-fatal
    }

    try {
      const { count } = await sb
        .from("photos")
        .select("id", { count: "exact", head: true })
        .eq("user_id", input.userId)
        .eq("batch_id", input.batchId);
      context.photos_in_cycle_so_far = count ?? 0;
    } catch {
      // non-fatal
    }
  }

  try {
    const { count: activeCycles } = await sb
      .from("farmer_cycles")
      .select("id", { count: "exact", head: true })
      .eq("user_id", input.userId)
      .eq("cycle_status", "active");
    const { count: activeBatches } = await sb
      .from("batches")
      .select("id", { count: "exact", head: true })
      .eq("user_id", input.userId)
      .in("batch_status", ["active", "placed"]);
    context.active_cycle_count = (activeCycles ?? 0) + (activeBatches ?? 0);
  } catch {
    // non-fatal
  }

  return context;
}

async function gatherWeather(lat: number, lng: number): Promise<PreCaptureWeather | null> {
  try {
    const owmKey =
      (import.meta.env?.VITE_OPENWEATHERMAP_KEY as string | undefined) ?? undefined;
    if (!owmKey) return null;

    const url =
      "https://api.openweathermap.org/data/2.5/weather?lat=" +
      encodeURIComponent(String(lat)) +
      "&lon=" +
      encodeURIComponent(String(lng)) +
      "&units=metric&appid=" +
      encodeURIComponent(owmKey);

    const resp = await fetch(url, { signal: AbortSignal.timeout(3000) });
    if (!resp.ok) return null;
    const data = (await resp.json()) as {
      main?: { temp?: number; feels_like?: number; humidity?: number; pressure?: number };
      wind?: { speed?: number; deg?: number };
      clouds?: { all?: number };
      visibility?: number;
      weather?: Array<{ main?: string; description?: string }>;
      rain?: Record<string, number>;
      sys?: { sunrise?: number; sunset?: number };
    };

    const now = Date.now() / 1000;
    const sunrise = typeof data?.sys?.sunrise === "number" ? data.sys.sunrise : null;
    const sunset = typeof data?.sys?.sunset === "number" ? data.sys.sunset : null;

    return {
      temp_c: data?.main?.temp ?? null,
      feels_like_c: data?.main?.feels_like ?? null,
      humidity_pct: data?.main?.humidity ?? null,
      pressure_hpa: data?.main?.pressure ?? null,
      wind_speed_mps: data?.wind?.speed ?? null,
      wind_direction_deg: data?.wind?.deg ?? null,
      cloud_cover_pct: data?.clouds?.all ?? null,
      visibility_meters: data?.visibility ?? null,
      weather_main: data?.weather?.[0]?.main ?? null,
      weather_description: data?.weather?.[0]?.description ?? null,
      rain_1h_mm: data?.rain?.["1h"] ?? null,
      uv_index: null,
      sunrise_utc: sunrise ? new Date(sunrise * 1000).toISOString() : null,
      sunset_utc: sunset ? new Date(sunset * 1000).toISOString() : null,
      is_daytime: sunrise != null && sunset != null ? now > sunrise && now < sunset : null,
      data_source: "openweathermap",
      fetched_at: new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export async function gatherPreCaptureMetadata(
  input: PreCaptureInput
): Promise<PreCaptureMetadata> {
  const now = Date.now();

  const conn = safeGet(
    () =>
      (navigator as unknown as { connection?: Record<string, unknown> })
        .connection
  );

  const locationPromise = getGeolocation();
  const agriculturalPromise = gatherAgriculturalContext(input);

  const location = await locationPromise;
  const weatherPromise =
    location && location.latitude != null && location.longitude != null
      ? gatherWeather(location.latitude, location.longitude)
      : Promise.resolve(null);

  const [agricultural_context, weather] = await Promise.all([
    agriculturalPromise,
    weatherPromise,
  ]);

  return {
    schema_version: 2,
    session_id: input.sessionId,
    session_started_at: input.sessionStartedAt,
    session_photo_count: input.sessionPhotoCount,
    time_in_camera_ms: now - input.cameraOpenedAt,

    user_id: input.userId,
    cycle_id: input.cycleId,
    batch_id: input.batchId,
    task_id: input.taskId,
    photo_category: String(input.photoCategory),

    device: {
      user_agent: safeGet(() => navigator.userAgent) ?? "unknown",
      platform: safeGet(() => navigator.platform) ?? "unknown",
      language: safeGet(() => navigator.language) ?? "unknown",
      timezone:
        safeGet(() => Intl.DateTimeFormat().resolvedOptions().timeZone) ??
        "unknown",
      screen_width: safeGet(() => window.screen.width) ?? 0,
      screen_height: safeGet(() => window.screen.height) ?? 0,
      pixel_ratio: safeGet(() => window.devicePixelRatio) ?? 1,
      online: safeGet(() => navigator.onLine) ?? true,
      connection_type: conn?.effectiveType as string | undefined,
      downlink_mbps: conn?.downlink as number | undefined,
      rtt_ms: conn?.rtt as number | undefined,
      save_data: conn?.saveData as boolean | undefined,
      memory_gb: safeGet(
        () =>
          (navigator as unknown as { deviceMemory?: number }).deviceMemory
      ),
      hardware_concurrency: safeGet(() => navigator.hardwareConcurrency),
    },

    camera: {
      is_front_camera: input.isFrontCamera,
      flash_mode: input.flashMode ?? undefined,
      zoom_level: input.zoomLevel ?? undefined,
    },

    location,

    client_timestamp: new Date(now).toISOString(),
    client_timestamp_ms: now,

    agricultural_context,
    weather,
  };
}
